/**
 * Custom assistant response action bar.
 *
 * Replaces the library's monolithic `<AssistantActionBar />` so we can:
 * 1. Relabel the reload button as "Regenerate" (the library hard-defaults it
 *    to "Refresh").
 * 2. Add a horizontal 3-dot overflow menu with run-scoped actions
 *    (copy run ID, open the run detail in a new tab).
 *
 * Reuses the `aui-assistant-action-bar-root` class so it inherits the
 * hover-to-show + spacing styling defined in `src/index.css`, and the same
 * `aui-button aui-button-ghost aui-button-icon` ghost buttons as the user
 * message action bar so the two read identically.
 */

import { MessagePrimitive, ActionBarPrimitive, useAuiState } from '@assistant-ui/react';
import {
  CopyIcon,
  CheckIcon,
  RefreshCwIcon,
  MoreHorizontal,
  SquareArrowOutUpRight,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu';
import { routes } from '../../lib/routes';

/** Run-scoped overflow menu. Only rendered when the message carries a runId. */
function ResponseOverflowMenu() {
  const messageMetadata = useAuiState((s) => s.message.metadata);
  const runId = (messageMetadata?.custom as Record<string, unknown> | undefined)?.runId as
    | string
    | undefined;

  if (!runId) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="More"
          aria-label="More actions"
          className="aui-button aui-button-ghost aui-button-icon"
        >
          <MoreHorizontal />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={4} onCloseAutoFocus={(e) => e.preventDefault()}>
        <DropdownMenuItem onClick={() => navigator.clipboard.writeText(runId)}>
          <CopyIcon className="mr-2 h-3.5 w-3.5" />
          Copy run ID
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.open(routes.console.run(runId), '_blank')}>
          <SquareArrowOutUpRight className="mr-2 h-3.5 w-3.5" />
          Open run detail
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AssistantResponseActionBar() {
  // Fixed-height wrapper that stays in flow at all times. `ActionBarPrimitive`
  // UNMOUNTS while any run is active (`hideWhenRunning`) and re-mounts at run
  // end — without a reserved-height host that would grow every prior message by
  // the action-bar height the moment a run finishes, shoving the scroll-locked
  // user message down. The `h-6` spacer mirrors `.agent-user-action-bar`.
  return (
    <div className="mt-1.5 flex h-6 items-center">
      <ActionBarPrimitive.Root
        hideWhenRunning
        className="aui-assistant-action-bar-root flex items-center gap-0.5"
      >
        <ActionBarPrimitive.Copy asChild>
        <button
          type="button"
          title="Copy"
          aria-label="Copy"
          className="aui-button aui-button-ghost aui-button-icon"
        >
          <MessagePrimitive.If copied>
            <CheckIcon />
          </MessagePrimitive.If>
          <MessagePrimitive.If copied={false}>
            <CopyIcon />
          </MessagePrimitive.If>
        </button>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <button
          type="button"
          title="Regenerate"
          aria-label="Regenerate"
          className="aui-button aui-button-ghost aui-button-icon"
        >
          <RefreshCwIcon />
        </button>
      </ActionBarPrimitive.Reload>
        <ResponseOverflowMenu />
      </ActionBarPrimitive.Root>
    </div>
  );
}
